import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MessageService } from 'primeng/api';
import { filter, interval } from 'rxjs';

/** Fréquence de vérification pendant qu'un onglet reste ouvert sans navigation (le service worker ne vérifie sinon qu'au démarrage/à la navigation). */
const POLL_INTERVAL_MS = 30 * 60 * 1000;
const RELOAD_DELAY_MS = 3000;

/**
 * Détecte les nouveaux déploiements pendant qu'une session reste ouverte, sans que l'utilisateur
 * ait besoin de savoir qu'un Ctrl+Shift+R est nécessaire (voir ngsw-config.json et
 * provideServiceWorker dans app.config.ts). N'a d'effet qu'en production : le service worker n'est
 * enregistré que pour la configuration "production" (angular.json), donc `swUpdate.isEnabled` est
 * `false` en développement et pendant le rendu SSR.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly messageService = inject(MessageService);

  init(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.applyUpdate());

    this.swUpdate.unrecoverable.subscribe(() => {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur de mise à jour',
        detail: "L'application doit être rechargée pour continuer.",
        sticky: true,
      });
      document.location.reload();
    });

    interval(POLL_INTERVAL_MS).subscribe(() => void this.swUpdate.checkForUpdate());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.swUpdate.checkForUpdate();
    });
  }

  private applyUpdate(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Nouvelle version disponible',
      detail: "L'application se met à jour…",
      life: RELOAD_DELAY_MS,
    });

    setTimeout(() => {
      this.swUpdate
        .activateUpdate()
        .catch(() => undefined)
        .finally(() => document.location.reload());
    }, RELOAD_DELAY_MS);
  }
}
